import type { PartnerApplicationStatus } from "@/types/application";
import { PARTNER_STATUS_LABELS } from "@/types/application";

export type StatusTone = "neutral" | "warning" | "review" | "approved" | "active" | "closed" | "rejected";

const STATUS_TONES: Record<PartnerApplicationStatus, StatusTone> = {
  registered: "neutral",
  business_docs_in_progress: "warning",
  business_docs_input_required: "warning",
  business_docs_submitted: "review",
  business_docs_verified: "review",
  kyc_in_progress: "review",
  kyc_submitted: "review",
  kyc_input_required: "warning",
  kyc_verified: "review",
  credit_review: "review",
  limit_offered: "approved",
  settlement_bank_updated: "approved",
  settlement_bank_verified: "approved",
  virtual_account_created: "approved",
  agreements_signed: "approved",
  account_change_verified: "approved",
  sanctioned: "approved",
  active: "active",
  closed: "closed",
  credit_rejected: "rejected",
};

export const STATUS_TONE_CLASS: Record<StatusTone, string> = {
  neutral: "bg-surface text-ink-soft ring-line",
  warning: "bg-warning-soft text-warning ring-warning/25",
  review: "bg-brand-soft text-brand-strong ring-brand/20",
  approved: "bg-success-soft text-success ring-success/25",
  active: "bg-success-soft text-success ring-success/25",
  closed: "bg-surface text-muted ring-line",
  rejected: "bg-danger-soft text-danger ring-danger/25",
};

export const STATUS_DOT_CLASS: Record<StatusTone, string> = {
  neutral: "bg-muted-soft",
  warning: "bg-warning",
  review: "bg-brand",
  approved: "bg-success",
  active: "bg-success",
  closed: "bg-muted-soft",
  rejected: "bg-danger",
};

export function isPartnerApplicationStatus(
  value: string,
): value is PartnerApplicationStatus {
  return value in PARTNER_STATUS_LABELS;
}

export function statusToneFor(status: string): StatusTone {
  if (isPartnerApplicationStatus(status)) {
    return STATUS_TONES[status];
  }
  return "neutral";
}

/**
 * True when credit has been approved.
 * `limit_offered` is the approved state; later statuses stay locked because credit remains approved.
 */
export function isCreditOfferApproved(status: string): boolean {
  const tone = statusToneFor(status);
  return tone === "approved" || tone === "active" || tone === "closed";
}

export function statusLabelFor(status: string, fallback?: string): string {
  if (fallback) return fallback;
  if (isPartnerApplicationStatus(status)) {
    return PARTNER_STATUS_LABELS[status];
  }
  return status.replaceAll("_", " ");
}
